/**
 * Trial Period Page
 * Página para gerenciar configurações de período trial (geral e por cliente)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Chip,
  Alert
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { TenantPickerList } from '@/components/platform/TenantPickerList';
import { apiService } from '@/services/api';
import { ClientCell } from '@/components/ClientCell';
import { useMessage } from '@/contexts/MessageContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useConfirmation } from '@/contexts/ConfirmationContext';
import { useFilters } from '@/contexts/FilterContext';
import { ProductCell } from '@/components/ProductCell';
import { useProductsMap } from '@/hooks/useProductsMap';
import { logger } from '@/utils/logger';
import { ProductScopeGate } from '@/components/ProductScopeGate';

export const TrialPeriodPage: React.FC = () => {
  const { productId } = useFilters();
  const productsById = useProductsMap();
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useMessage();
  const { setRefreshCallback } = useRefresh();
  const { confirm } = useConfirmation();

  // Modal de edição por cliente
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<{
    tenantId: string;
    trialPeriodDays: number;
  } | null>(null);
  const [editDays, setEditDays] = useState<string>('');
  const [savingTenant, setSavingTenant] = useState(false);

  // Modal de adicionar cliente
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedAddTenantId, setSelectedAddTenantId] = useState<string | null>(null);
  const [newTenantDays, setNewTenantDays] = useState<string>('30');
  const [savingNew, setSavingNew] = useState(false);
  const [tenants, setTenants] = useState<any[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!productId?.trim()) return;
    setLoading(true);
    setLoadError(null);
    try {
      const response = await apiService.getTrialPeriodConfig();
      if (response.data.success) {
        setConfig(response.data.data);
      }
    } catch (error) {
      setConfig(null);
      setLoadError(error);
      logger.error('Error loading trial period config:', error);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig, productId]);

  const loadTenants = useCallback(async () => {
    setTenantsLoading(true);
    try {
      const res = await apiService.getTenants({ page: 1, limit: 1000, productId });
      if (res.data?.success && res.data?.data) {
        setTenants(res.data.data);
      }
    } catch {
      setTenants([]);
    } finally {
      setTenantsLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (config) {
      loadTenants();
    }
  }, [config, loadTenants]);

  useEffect(() => {
    setRefreshCallback(loadConfig);
    return () => setRefreshCallback(null);
  }, [loadConfig, setRefreshCallback]);

  const handleSaveDefault = async () => {
    const days = config.defaultTrialPeriodDays;
    if (
      days === undefined ||
      days === null ||
      days === '' ||
      Number.isNaN(Number(days)) ||
      Number(days) < 0
    ) {
      showError('Informe um número válido de dias (0 ou mais).');
      return;
    }
    const daysNum = Number(days);
    setSaving(true);
    try {
      const response = await apiService.updateDefaultTrialPeriod(daysNum);
      if (response.data.success) {
        showSuccess('Período de teste padrão atualizado com sucesso!');
        setConfig(response.data.data);
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao salvar configuração');
      logger.error('Error saving trial period config:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (tc: { tenantId: string; trialPeriodDays: number }) => {
    setEditingTenant(tc);
    setEditDays(String(tc.trialPeriodDays));
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setEditingTenant(null);
    setEditDays('');
  };

  const handleSaveTenant = async () => {
    if (!editingTenant) return;
    const daysNum = parseInt(editDays, 10);
    if (Number.isNaN(daysNum) || daysNum < 0) {
      showError('Informe um número válido de dias (0 ou mais).');
      return;
    }
    setSavingTenant(true);
    try {
      const response = await apiService.updateTenantTrialPeriod(editingTenant.tenantId, daysNum);
      if (response.data.success) {
        showSuccess('Configuração do cliente atualizada com sucesso!');
        setConfig(response.data.data);
        handleCloseEdit();
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao salvar configuração do cliente');
    } finally {
      setSavingTenant(false);
    }
  };

  const handleOpenAdd = async () => {
    setSelectedAddTenantId(null);
    setNewTenantDays(
      config?.defaultTrialPeriodDays != null ? String(config.defaultTrialPeriodDays) : '30'
    );
    setAddModalOpen(true);
    await loadTenants();
  };

  const handleCloseAdd = () => {
    setAddModalOpen(false);
    setSelectedAddTenantId(null);
    setNewTenantDays('30');
  };

  const handleSaveNewTenant = async () => {
    const tenantId = selectedAddTenantId?.trim();
    if (!tenantId) {
      showError('Selecione um cliente na lista.');
      return;
    }
    const daysNum = parseInt(newTenantDays, 10);
    if (Number.isNaN(daysNum) || daysNum < 0) {
      showError('Informe um número válido de dias (0 ou mais).');
      return;
    }
    setSavingNew(true);
    try {
      const response = await apiService.updateTenantTrialPeriod(tenantId, daysNum);
      if (response.data.success) {
        showSuccess('Configuração do cliente adicionada com sucesso!');
        setConfig(response.data.data);
        handleCloseAdd();
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao adicionar configuração do cliente');
    } finally {
      setSavingNew(false);
    }
  };

  const handleRemoveTenant = (tenantId: string) => {
    confirm({
      title: 'Remover configuração do cliente',
      message: `Deseja remover a configuração personalizada de período de teste para este cliente? O cliente passará a usar o período padrão.`,
      confirmText: 'Remover',
      cancelText: 'Cancelar',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await apiService.removeTenantTrialPeriod(tenantId);
          showSuccess('Configuração do cliente removida com sucesso!');
          loadConfig();
        } catch (error: any) {
          showError(error.response?.data?.message || 'Erro ao remover configuração');
        }
      }
    });
  };

  return (
    <ProductScopeGate
      productId={productId}
      integration="core"
      loading={loading}
      loadError={loadError}
      isEmpty={false}
      onRetry={loadConfig}
    >
      {!config ? (
        <Alert severity="info">
          Configuração de período de teste ainda não disponível para este produto. Se o problema
          persistir após recarregar, verifique o registry e o banco core do admin.
        </Alert>
      ) : (
        <Box>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="h4">Período de teste</Typography>
                <Typography variant="body2" color="text.secondary">
                  Define o trial padrão do produto e exceções por cliente. Útil para onboarding,
                  campanhas e contas de demonstração controladas.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <ProductCell
                    productId={productId}
                    name={productsById.get(productId)?.displayName}
                    avatarUrl={productsById.get(productId)?.avatarUrl}
                  />
                  <Chip size="small" variant="outlined" label="Padrão + exceções por cliente" />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Período Trial Padrão
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Este valor será usado para novos clientes que não possuírem configuração específica.
            </Typography>
            <TextField
              fullWidth
              label="Dias (padrão)"
              type="number"
              value={config.defaultTrialPeriodDays ?? ''}
              onChange={e => {
                const v = e.target.value;
                setConfig({
                  ...config,
                  defaultTrialPeriodDays: v === '' ? undefined : parseInt(v, 10)
                });
              }}
              inputProps={{ min: 0, step: 1 }}
              margin="normal"
              helperText="Número de dias do período de teste padrão"
            />
            <Button
              variant="contained"
              onClick={handleSaveDefault}
              disabled={saving}
              sx={{ mt: 2 }}
            >
              {saving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Salvando...
                </>
              ) : (
                'Salvar Padrão'
              )}
            </Button>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mb: 2
              }}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  Configurações por Cliente
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure um período de teste específico por cliente. Clientes não listados usam o
                  período padrão.
                </Typography>
              </Box>
              <Button variant="outlined" startIcon={<Add />} onClick={handleOpenAdd}>
                Adicionar cliente
              </Button>
            </Box>
            {config.tenantConfigs && config.tenantConfigs.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Dias</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {config.tenantConfigs.map((tc: any, index: number) => {
                      const tenant = tenants.find((t: any) => t.tenantId === tc.tenantId);
                      return (
                        <TableRow key={tc.tenantId || index}>
                          <TableCell>
                            <ClientCell
                              name={tenant?.companyName}
                              tenantId={tc.tenantId}
                              logoUrl={(tenant as any)?.logoUrl}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{tc.trialPeriodDays}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(tc)}
                              title="Editar"
                              color="primary"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveTenant(tc.tenantId)}
                              title="Remover (usar padrão)"
                              color="error"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhuma configuração personalizada. Todos os clientes usam o período de teste
                padrão.
              </Typography>
            )}
          </Paper>

          <Dialog open={addModalOpen} onClose={handleCloseAdd} maxWidth="sm" fullWidth>
            <DialogTitle>Adicionar configuração por cliente</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Apenas clientes deste produto sem período de teste personalizado.
              </Typography>
              <TenantPickerList
                tenants={tenants.map(
                  (t: { tenantId: string; companyName?: string; logoUrl?: string }) => ({
                    tenantId: String(t.tenantId),
                    ...(t.companyName ? { companyName: String(t.companyName) } : {}),
                    ...(t.logoUrl ? { logoUrl: String(t.logoUrl) } : {})
                  })
                )}
                excludeTenantIds={(config.tenantConfigs || []).map(
                  (tc: { tenantId: string }) => tc.tenantId
                )}
                loading={tenantsLoading}
                onSelect={tenantId => setSelectedAddTenantId(tenantId)}
                emptyMessage="Todos os clientes já possuem configuração personalizada. Edite na lista abaixo."
              />
              {selectedAddTenantId && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selecionado: <strong>{selectedAddTenantId}</strong>
                </Typography>
              )}
              <TextField
                fullWidth
                label="Dias de teste"
                type="number"
                value={newTenantDays}
                onChange={e => setNewTenantDays(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                margin="normal"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseAdd} disabled={savingNew}>
                Cancelar
              </Button>
              <Button variant="contained" onClick={handleSaveNewTenant} disabled={savingNew}>
                {savingNew ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Salvando...
                  </>
                ) : (
                  'Adicionar'
                )}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={editModalOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
            <DialogTitle>Editar período de teste do cliente</DialogTitle>
            <DialogContent>
              {editingTenant && (
                <Box sx={{ pt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Cliente: <strong>{editingTenant.tenantId}</strong>
                  </Typography>
                  <TextField
                    fullWidth
                    label="Dias"
                    type="number"
                    value={editDays}
                    onChange={e => setEditDays(e.target.value)}
                    inputProps={{ min: 0, step: 1 }}
                    margin="normal"
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEdit} disabled={savingTenant}>
                Cancelar
              </Button>
              <Button variant="contained" onClick={handleSaveTenant} disabled={savingTenant}>
                {savingTenant ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </ProductScopeGate>
  );
};

export default TrialPeriodPage;
